import { Fragment, FunctionComponent, useCallback, useEffect, useState } from 'react';
import { Text } from '@react-three/drei';
import { useProvider } from 'wagmi';
import { ethers } from 'ethers';
import { OnlyBotMetadata } from '@animavirtuality/onlybots-core';
import { constArrayIncludes, typedNotUndefined } from '@animavirtuality/utils';
import { IPFS_MIRROR, tokenContractAddress } from '@/lib/globals';
import { useRouterContext } from '@/context/RouterContext';
import OnlyBotsToken from '@/contracts/OnlyBotsToken.json';
import { useCollectionInfo } from '@/hooks/useCollectionInfo';
import { PlaystationButton } from '@/components/PlaystationButton';
import { useResponsive } from '@/hooks/useResponsive';
import { helveticaNeue } from '@/lib/fonts';

const IPFS = 'ipfs://';

const ASSET_NAMES = [
    'cover-v2',
    'cover-v2-noqr',
    'cover-v2-transparent',
    'cover-noqr',
    'cover',
    'static-cover-noqr',
    'static-cover',
    'gltf',
] as const;
type AssetName = (typeof ASSET_NAMES)[number];

const SUFFIXES: Record<AssetName, string> = {
    'cover-v2': '-qr.png',
    'cover-v2-noqr': '.png',
    'cover-v2-transparent': '.webp',
    cover: '-qr.mp4',
    'cover-noqr': '.mp4',
    'static-cover': '-qr.png',
    'static-cover-noqr': '.png',
    gltf: '.gltf',
};

const LABELS: Record<AssetName, string> = {
    'cover-v2': 'Static Cover w/ QR',
    'cover-v2-noqr': 'Static Cover',
    'cover-v2-transparent': 'Static Transparent Cover',
    cover: 'Legacy Animated Cover w/ QR',
    'cover-noqr': 'Legacy Animated Cover',
    'static-cover': 'Legacy Static Cover w/ QR',
    'static-cover-noqr': 'Legacy Static Cover',
    gltf: '3D Model (GLTF)',
};

export const Download: FunctionComponent = () => {
    const provider = useProvider();
    const { tokenId, navigate } = useRouterContext();
    const { totalSupply } = useCollectionInfo();
    const { responsiveValue } = useResponsive();

    const [name, setName] = useState<string | null>(null);
    const [assets, setAssets] = useState<Partial<Record<AssetName, string>> | null>(null);

    const fetchMetadata = useCallback(async () => {
        if (tokenId && totalSupply) {
            try {
                const contract = new ethers.Contract(tokenContractAddress, OnlyBotsToken.abi, provider);
                const tokenURI = await contract.tokenURI(tokenId);
                if (tokenURI.startsWith(IPFS)) {
                    const path = tokenURI.slice(IPFS.length);
                    const tokenJsonURI = new URL(`${IPFS_MIRROR}${path}`);
                    const response = await fetch(tokenJsonURI);
                    const { name, assets }: { name: string; assets: OnlyBotMetadata['assets'] } = JSON.parse(
                        await response.text()
                    );

                    const assetMap: Partial<Record<AssetName, string>> = {};
                    for (const asset of assets) {
                        if (constArrayIncludes(ASSET_NAMES, asset.name)) {
                            let url = asset.uri;
                            if (url.startsWith(IPFS)) {
                                url = `${IPFS_MIRROR}dl/${url.slice(IPFS.length)}?f=${encodeURIComponent(
                                    name + SUFFIXES[asset.name]
                                )}`;
                            }
                            assetMap[asset.name as AssetName] = url;
                        }
                    }

                    setName(name);
                    setAssets(assetMap);
                }
            } catch (e: any) {
                console.error(e);
            }
        }
    }, [provider, tokenId, totalSupply]);

    // if tokenID is greater than supply, send to not-found
    useEffect(() => {
        if (tokenId && totalSupply) {
            if (tokenId <= totalSupply) {
                void fetchMetadata();
            } else {
                navigate('/not-found');
            }
        }
    }, [tokenId, totalSupply]); // eslint-disable-line react-hooks/exhaustive-deps

    const offsetPerItem = 0.8;
    const mid = assets === null ? 0 : (Object.values(assets).filter(typedNotUndefined).length + 1) / 2;
    let current = 1;

    if (assets === null) {
        return null;
    }

    return (
        <>
            <group scale={responsiveValue(0.5, 0.8)} position={[responsiveValue(-0.7, -1.2), 0, 0]}>
                <Text font={helveticaNeue} fontSize={0.4} position={[1.25, mid * offsetPerItem, 0]}>
                    #{tokenId}: {name}
                </Text>
                {ASSET_NAMES.map((assetName) => {
                    const url = assets[assetName];
                    if (!url) {
                        return <Fragment key={assetName} />;
                    }

                    return (
                        <Fragment key={assetName}>
                            <PlaystationButton
                                icon={'download'}
                                position={[0, -(current++ - mid) * offsetPerItem, 0]}
                                label={LABELS[assetName]}
                                clickableAreaDimensions={[3.7, 0.6]}
                                action={() => {
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                }}
                            />
                        </Fragment>
                    );
                })}
            </group>
        </>
    );
};
